

import { Button } from '@/components/ui/button';
import { Edit, Eye, EyeOff, ThumbsUp, Video, StickyNote } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';

interface TechniqueCardFooterProps {
  hasNotes?: boolean;
  hasVideos?: boolean;
  isEditable?: boolean;
  isUserCreated?: boolean;
  isLiked?: boolean;
  isHidden?: boolean;
  likeCount?: number;
  likeAnimation?: boolean;
  isLiking?: boolean;
  isHiding?: boolean;
  onLikeToggle?: (e: React.MouseEvent) => void;
  onHideToggle?: (e: React.MouseEvent) => void;
  onEdit?: (e: React.MouseEvent) => void;
}

export const TechniqueCardFooter: React.FC<TechniqueCardFooterProps> = ({
  hasNotes = false,
  hasVideos = false,
  isEditable = false,
  isUserCreated = false,
  isLiked = false,
  isHidden = false,
  isLiking = false,
  isHiding = false,
  onLikeToggle,
  onHideToggle,
  onEdit,
}) => {
  return (
    <div className="w-full flex flex-wrap items-center justify-between gap-1">
      <div className="flex items-center space-x-1 text-muted-foreground">
        {hasNotes && (
          <Badge variant="outline" className="flex items-center gap-1 h-6 px-1.5 text-[10px] sm:text-xs">
            <StickyNote className="h-3 w-3" />
            <span className="hidden sm:inline">Notes</span>
          </Badge>
        )}
        
        {hasVideos && (
          <Badge variant="outline" className="flex items-center gap-1 h-6 px-1.5 text-[10px] sm:text-xs">
            <Video className="h-3 w-3" />
            <span className="hidden sm:inline">Videos</span>
          </Badge>
        )}
      </div>
      
      <div className="flex items-center space-x-1">
        {onLikeToggle && (
          <Button
            size="sm"
            variant="ghost"
            className={cn(
              "h-7 w-7 p-0 text-muted-foreground",
              isLiked && "text-primary",
              isLiking && "animate-pulse pointer-events-none"
            )}
            onClick={onLikeToggle}
            disabled={isLiking}
          >
            <ThumbsUp className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="sr-only">Like</span>
          </Button>
        )}
        
        {isUserCreated && onHideToggle && (
          <Button
            size="sm"
            variant="ghost"
            className={cn(
              "h-7 w-7 p-0 text-muted-foreground",
              isHidden && "text-gray-700",
              isHiding && "animate-pulse pointer-events-none"
            )}
            onClick={onHideToggle}
            disabled={isHiding}
          >
            {isHidden ? (
              <EyeOff className="h-3 w-3 sm:h-4 sm:w-4" />
            ) : (
              <Eye className="h-3 w-3 sm:h-4 sm:w-4" />
            )}
            <span className="sr-only">{isHidden ? 'Show' : 'Hide'}</span>
          </Button>
        )}
        
        {isEditable && onEdit && (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 w-7 p-0 text-muted-foreground"
            onClick={onEdit}
          >
            <Edit className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="sr-only">Edit</span>
          </Button>
        )}
      </div>
    </div>
  );
};
